/**
 * Turn the PrepAI / extraction-engine JSON shape into the shape the bulk
 * upload API takes.
 *
 * The engine's export (the PrepAI shape in `maths_jamb_2018.json`):
 *   { number, question_text, question_type, options: '{"A":"…"}' (a JSON
 *     string), correct_answer, explanation, exam_type, exam_year,
 *     question_source: "pastq", image_url: ["figures/…png"] | null,
 *     topic, subtopic }
 *
 * The upload API wants camelCase, `options` as an object, ids for subject /
 * topic / subtopic, and images as a full URL or embedded data. Names are
 * matched to ids case-insensitively; anything that doesn't match is left for
 * the reviewer to pick in the table, as with any JSON file.
 */

export interface UploadQuestion {
  questionText?: string;
  questionType?: string;
  subjectId?: string;
  topicId?: string;
  subTopicId?: string;
  options?: Record<string, string>;
  correctAnswer?: string;
  explanation?: string;
  examType?: string;
  examYear?: number;
  questionSource?: string;
  imageUrl?: string;
  /** The engine's relative image path, kept so the reviewer can attach the file. */
  imagePath?: string;
  /** The PrepAI topic/subtopic names, shown when no matching id was found. */
  topicName?: string;
  subtopicName?: string;
  /** The question number in the book, for the table and error messages. */
  number?: number;
}

export interface NamedRef {
  id: string;
  name: string;
}

export interface Lookups {
  subjects: (NamedRef & { exam_type?: string })[];
  /** Topics for the chosen subject. */
  topics: NamedRef[];
  /** Subtopics by topic id. */
  subtopics: Record<string, NamedRef[]>;
}

/** True when the file is the PrepAI/engine shape rather than our own. */
export function isPrepAiShape(items: unknown[]): boolean {
  const first = items[0] as Record<string, unknown> | undefined;
  return Boolean(first && ("question_text" in first || "correct_answer" in first));
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");
const byName = <T extends NamedRef>(list: T[], name: string | null | undefined) =>
  name ? list.find((x) => norm(x.name) === norm(name)) : undefined;

/**
 * A name reduced to its letters, so the forms a file uses for one subject or
 * topic agree: "Literature-in-English", "literature in english" and
 * "Literature In English" are one name, and "Number & Numeration" is
 * "number numeration". The same loose match the PQX engine uses.
 */
const loose = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z]/g, "");

/** Short names that files use for a subject the bank spells out. */
const SUBJECT_ALIASES: Record<string, string> = {
  maths: "mathematics",
  math: "mathematics",
  furthermaths: "furthermathematics",
  english: "englishlanguage",
  lit: "literatureinenglish",
  literature: "literatureinenglish",
  crs: "christianreligiousstudies",
  crk: "christianreligiousstudies",
  irs: "islamicreligiousstudies",
  irk: "islamicreligiousstudies",
  govt: "government",
  econs: "economics",
  agric: "agriculturalscience",
  agriculture: "agriculturalscience",
};

const subjectKey = (s: string) => {
  const key = loose(s);
  return SUBJECT_ALIASES[key] ?? key;
};

/** A topic or subtopic by name: exactly, else loosely (case, spacing, "&", hyphens). */
const looseByName = <T extends NamedRef>(list: T[], name: string | null | undefined) => {
  if (!name) return undefined;
  const exact = byName(list, name);
  if (exact) return exact;
  const wanted = loose(name);
  return wanted ? list.find((x) => loose(x.name) === wanted) : undefined;
};

/**
 * The subject a question file is for, found the ways a worker would expect:
 *
 *  1. a `subject` field on the questions (the engine's verify output and some
 *     hand-made files carry one);
 *  2. the file's name, as the engine names its exports
 *     (`mathematics_jamb_2015.json`, `Literature-in-English_waec.json`);
 *  3. a question whose topic is the subject's own name, which the engine uses
 *     when it has no finer topic.
 *
 * Only a subject the file clearly points at is picked; an unclear file is left
 * for the worker to choose. A subject offered for the file's exam is preferred
 * when two share a name.
 */
export function guessSubject<S extends NamedRef & { exam_type?: string }>(
  items: Record<string, unknown>[],
  subjects: S[],
  filename = "",
): string | undefined {
  const exam = String(items[0]?.exam_type ?? "").toUpperCase();
  const pick = (key: string) => {
    const matches = subjects.filter((s) => subjectKey(s.name) === key);
    return (
      matches.find((s) => !s.exam_type || s.exam_type === exam || s.exam_type === "BOTH") ??
      matches[0]
    )?.id;
  };

  // 1. A subject the questions name. Every question naming the same one.
  const named = new Set(
    items.map((i) => (i.subject ? subjectKey(String(i.subject)) : "")).filter(Boolean),
  );
  if (named.size === 1) {
    const found = pick([...named][0]!);
    if (found) return found;
  }

  // 2. The file's name: each part before the exam or year, then all of them joined.
  const stem = filename.replace(/\.json$/i, "");
  if (stem) {
    const parts = stem.split(/[_\s.]+/).filter(Boolean);
    const cut = parts.findIndex((p) => /^(jamb|waec|neco|\d{4}|none|ai|pastq)$/i.test(p));
    const before = (cut < 0 ? parts : parts.slice(0, cut)).join(" ");
    for (const candidate of [before, ...parts]) {
      const found = candidate ? pick(subjectKey(candidate)) : undefined;
      if (found) return found;
    }
  }

  // 3. A topic that is the subject's own name.
  for (const item of items) {
    const found = item.topic ? pick(subjectKey(String(item.topic))) : undefined;
    if (found) return found;
  }
  return undefined;
}

const SOURCE: Record<string, Record<string, string>> = {
  JAMB: { pastq: "jamb_past_question", ai: "jamb_ai_generated" },
  WAEC: { pastq: "waec_past_question", ai: "waec_ai_generated" },
};

function parseOptions(raw: unknown): Record<string, string> | undefined {
  const value = typeof raw === "string" ? safeJson(raw) : raw;
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const entries = Object.entries(value as Record<string, unknown>)
    .map(([k, v]) => [k.trim().toUpperCase(), String(v ?? "").trim()] as const)
    // The engine sometimes emits a blank fifth or fourth option; drop it so the
    // reviewer sees a missing option flagged rather than an empty choice saved.
    .filter(([, v]) => v !== "");
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export function fromPrepAi(
  item: Record<string, unknown>,
  subjectId: string,
  lookups: Lookups,
): UploadQuestion {
  const exam = String(item.exam_type ?? "").toUpperCase();
  const source = String(item.question_source ?? "").toLowerCase();
  const images = Array.isArray(item.image_url)
    ? item.image_url
    : item.image_url
      ? [item.image_url]
      : [];
  const firstImage = images.map(String).find(Boolean);
  const isUrl = firstImage ? /^(https?:|data:image\/)/i.test(firstImage) : false;

  // A topic of "Mathematics" (the subject's own name) carries no information;
  // the engine uses it when it has no finer topic, so the subtopic is the topic.
  const subject = lookups.subjects.find((s) => s.id === subjectId);
  const rawTopic = item.topic ? String(item.topic) : undefined;
  const rawSub = item.subtopic ? String(item.subtopic) : undefined;
  const topicName =
    rawTopic && subject && loose(rawTopic) === loose(subject.name) ? rawSub : rawTopic;
  const subtopicName = topicName === rawSub ? undefined : rawSub;

  // The topic by name, loosely; then its subtopic under it. When the file
  // names no topic we know, a subtopic held by exactly one topic places the
  // question under that topic. A known topic is never overridden.
  let topic = looseByName(lookups.topics, topicName);
  let subtopic = topic ? looseByName(lookups.subtopics[topic.id] ?? [], subtopicName) : undefined;
  if (!topic && subtopicName) {
    const holders = lookups.topics.filter((t) =>
      looseByName(lookups.subtopics[t.id] ?? [], subtopicName),
    );
    if (holders.length === 1) {
      topic = holders[0]!;
      subtopic = looseByName(lookups.subtopics[topic.id] ?? [], subtopicName);
    }
  }

  const out: UploadQuestion = {
    subjectId,
    questionText: String(item.question_text ?? "").trim(),
    questionType: String(item.question_type ?? "mcq"),
  };
  const options = parseOptions(item.options);
  if (options) out.options = options;
  if (item.correct_answer) out.correctAnswer = String(item.correct_answer).trim().toUpperCase();
  if (item.explanation) out.explanation = String(item.explanation);
  if (exam === "JAMB" || exam === "WAEC") out.examType = exam;
  if (item.exam_year) out.examYear = Number(item.exam_year);
  const mapped = SOURCE[exam]?.[source];
  if (mapped) out.questionSource = mapped;
  if (firstImage && isUrl) out.imageUrl = firstImage;
  if (firstImage && !isUrl) out.imagePath = firstImage;
  if (topic) out.topicId = topic.id;
  if (subtopic) out.subTopicId = subtopic.id;
  if (topicName) out.topicName = topicName;
  if (subtopicName) out.subtopicName = subtopicName;
  if (item.number !== undefined) out.number = Number(item.number);
  return out;
}

/**
 * Match image files the reviewer dropped alongside the JSON to the
 * questions' `figures/…png` paths, by file name.
 */
export function attachImageFiles(
  questions: UploadQuestion[],
  files: { name: string; dataUri: string }[],
): { questions: UploadQuestion[]; missing: string[] } {
  const byFile = new Map(files.map((f) => [f.name.toLowerCase(), f.dataUri]));
  const missing: string[] = [];
  const next = questions.map((q) => {
    if (!q.imagePath || q.imageUrl) return q;
    const file = q.imagePath.split("/").pop()!.toLowerCase();
    const dataUri = byFile.get(file);
    if (!dataUri) {
      missing.push(q.imagePath);
      return q;
    }
    return { ...q, imageUrl: dataUri };
  });
  return { questions: next, missing };
}
